// src/api/wordpress.ts
import { BlogArticle } from '../types/blog';

const API_URL = 'https://www.ludyworks.com/wp-json/wp/v2/posts';
const NO_IMAGE_URL = '/images/Blog/NOIMAGE.svg';

const getAuthToken = (): string => {
  const username = "ludyworks";
  const password = "vKjD3W!UuDNMJJQ";

  if (!username || !password) {
    throw new Error('WordPressのユーザー名またはパスワードが設定されていません。');
  }

  const encodedCredentials = btoa(`${username}:${password}`);
  return `Basic ${encodedCredentials}`;
};

export const fetchWordPressPosts = async (
  page = 1, 
  perPage = 10, 
  categoryId?: number, 
  searchTerm?: string
): Promise<{ articles: BlogArticle[], totalPages: number, totalPosts: number }> => {
  try {
    const authToken = getAuthToken();
    
    let url = `${API_URL}?_embed&page=${page}&per_page=${perPage}`;
    if (categoryId) {
      url += `&categories=${categoryId}`;
    }
    if (searchTerm) {
      url += `&search=${encodeURIComponent(searchTerm)}`;
    }

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: authToken,
      },
    });

    if (!response.ok) {
      throw new Error(`APIリクエストに失敗しました: ${response.status}`);
    }

    const totalPosts = parseInt(response.headers.get('X-WP-Total') || '0', 10);
    const totalPages = parseInt(response.headers.get('X-WP-TotalPages') || '0', 10);

    const data = await response.json();

    // WordPressのレスポンスデータをBlogArticle型に変換
    const articles: BlogArticle[] = data.map((item: any) => {
      const imageUrl = item._embedded?.['wp:featuredmedia']?.[0]?.source_url || NO_IMAGE_URL;
      const category = item._embedded?.['wp:term']?.[0]?.[0]?.name || '未分類';

      return {
        id: String(item.id),
        slug: item.slug,
        title: item.title.rendered,
        excerpt: item.excerpt.rendered,
        content: item.content.rendered,
        imageUrl: imageUrl,
        category: category,
        categoryIds: item.categories || [],
        tags: item.tags ? item.tags : [],
        author: item.author ? item.author : '不明',
        publishedAt: item.date,
      };
    });

    return { articles, totalPages, totalPosts };
  } catch (error: any) {
    console.error('WordPress APIからのデータ取得エラー:', error);
    throw error;
  }
};

// IDに基づいて単一のWordPress記事を取得する関数（URLの構造からslugはIDとして扱う）
export const fetchWordPressPostById = async (id: string): Promise<BlogArticle | null> => {
  try {
    const authToken = getAuthToken();
    // _embedパラメータを追加して、アイキャッチ画像などの関連データを埋め込む
    const response = await fetch(`${API_URL}/${id}?_embed`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: authToken,
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null; // 記事が見つからない場合はnullを返す
      }
      throw new Error(`APIリクエストに失敗しました: ${response.status}`);
    }

    const item = await response.json();

    // WordPressのレスポンスデータをBlogArticle型に変換
    // _embeddedからアイキャッチ画像のURLを取得
    const imageUrl = item._embedded?.['wp:featuredmedia']?.[0]?.source_url || NO_IMAGE_URL;
    // _embeddedからカテゴリ名を取得
    const category = item._embedded?.['wp:term']?.[0]?.[0]?.name || '未分類';

    const article: BlogArticle = {
      id: String(item.id),
      slug: item.slug,
      title: item.title.rendered,
      excerpt: item.excerpt.rendered,
      content: item.content.rendered,
      imageUrl: imageUrl,
      category: category,
      categoryIds: item.categories || [],
      tags: item.tags ? item.tags : [],
      author: item.author ? item.author : '不明',
      publishedAt: item.date,
    };

    return article;
  } catch (error: any) {
    console.error(`ID "${id}" の記事取得エラー:`, error);
    throw error;
  }
};

// WordPressのカテゴリ一覧を取得する関数
export const fetchWordPressCategories = async (): Promise<{ id: number; name: string }[]> => {
  try {
    const authToken = getAuthToken();
    const response = await fetch('https://www.ludyworks.com/wp-json/wp/v2/categories', {
      headers: {
        'Content-Type': 'application/json',
        Authorization: authToken,
      },
    });

    if (!response.ok) {
      throw new Error(`APIリクエストに失敗しました: ${response.status}`);
    }

    const data = await response.json();
    
    // 不要な「未分類」カテゴリを除外
    const filteredData = data.filter((cat: any) => cat.slug !== 'uncategorized');

    return filteredData.map((cat: any) => ({
      id: cat.id,
      name: cat.name,
    }));
  } catch (error: any) {
    console.error('WordPressカテゴリの取得エラー:', error);
    throw error;
  }
};
